import React from "react"
import { UserProfile, ActiveView } from "../types/objects"
import { BarChart3, Check, ArrowRight, LayoutDashboard } from "lucide-react"

/**
 * HeaderProps interface
 */
interface HeaderProps {
    data?: {
        eyebrow?: string
        title: string
        paragraph: string
        benefits?: string[]
        cta?: string
        ctaSecondary?: string
    }
    user: UserProfile | null
    setView: (view: ActiveView) => void
}

/** Satellite systems drawn around the hub in the hero illustration */
const NET_NODES: { x: number; y: number; label: string; sub: string }[] = [
    { x: 260, y: 66, label: "AI modely", sub: "LLM / API" },
    { x: 402, y: 148, label: "E-shop", sub: "objednávky" },
    { x: 402, y: 312, label: "Účetnictví", sub: "faktury" },
    { x: 260, y: 394, label: "Make / API", sub: "adaptéry" },
    { x: 118, y: 312, label: "ERP", sub: "sklad, výroba" },
    { x: 118, y: 148, label: "CRM", sub: "obchod" }
]

const HUB = { x: 260, y: 230 }

/**
 * Animated integration diagram: systems connected to a central hub with flowing data.
 */
const IntegrationNetwork: React.FC = () => (
    <svg viewBox="0 0 520 460" role="img" aria-label="Schéma propojení firemních systémů přes integrační hub">
        <defs>
            <linearGradient id="lp-net-grad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#5ca9fb" />
                <stop offset="100%" stopColor="#67e8f9" />
            </linearGradient>
            <linearGradient id="lp-hub-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#5ca9fb" />
                <stop offset="100%" stopColor="#6372ff" />
            </linearGradient>
            <filter id="lp-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="8" result="blur" />
                <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
        </defs>

        {/* Expanding rings behind the hub */}
        <circle className="lp-net__ring" cx={HUB.x} cy={HUB.y} r={46} />
        <circle className="lp-net__ring lp-net__ring--2" cx={HUB.x} cy={HUB.y} r={46} />

        {/* Static links + animated data flow */}
        {NET_NODES.map(node => (
            <g key={`link-${node.label}`}>
                <line className="lp-net__link" x1={node.x} y1={node.y} x2={HUB.x} y2={HUB.y} />
                <line className="lp-net__flow" x1={node.x} y1={node.y} x2={HUB.x} y2={HUB.y} />
            </g>
        ))}

        {/* Satellite nodes */}
        {NET_NODES.map((node, i) => (
            <g key={node.label} className="lp-net__node" style={{ animationDelay: `${-i}s` }}>
                <circle cx={node.x} cy={node.y} r={40} fill="rgba(11, 18, 36, 0.9)" stroke="rgba(255,255,255,0.18)" strokeWidth={1.5} />
                <circle cx={node.x} cy={node.y} r={40} fill="rgba(99, 114, 255, 0.12)" />
                <text className="lp-net__label" x={node.x} y={node.y + 1}>{node.label}</text>
                <text className="lp-net__sub" x={node.x} y={node.y + 16}>{node.sub}</text>
            </g>
        ))}

        {/* Central hub */}
        <g filter="url(#lp-glow)">
            <circle cx={HUB.x} cy={HUB.y} r={52} fill="url(#lp-hub-grad)" />
        </g>
        <circle cx={HUB.x} cy={HUB.y} r={52} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} />
        <image href="/img/b2bticon.png" x={HUB.x - 26} y={HUB.y - 34} width={52} height={52} style={{ borderRadius: 12 }} />
        <text className="lp-net__label" x={HUB.x} y={HUB.y + 34} style={{ fontSize: 11 }}>B2B Insight</text>
    </svg>
)

/**
 * Splits the hero title on the dash so the second half can be highlighted
 * with a gradient without changing the copy.
 */
const renderTitle = (title: string) => {
    const separator = " – "
    const idx = title.indexOf(separator)
    if (idx === -1) return title
    return (
        <>
            {title.slice(0, idx)}
            {separator}
            <em>{title.slice(idx + separator.length)}</em>
        </>
    )
}

/**
 * Header Component
 * Full-screen hero with animated background and dynamic CTA based on auth state.
 */
export const Header: React.FC<HeaderProps> = ({ data, user, setView }) => {
    const title = data?.title || "Optimalizujeme firemní procesy a integrujeme systémy s podporou AI"

    return (
        <header id="header" className="lp-hero">
            <div className="lp-hero__grid" aria-hidden="true" />
            <div className="lp-hero__blob lp-hero__blob--1" aria-hidden="true" />
            <div className="lp-hero__blob lp-hero__blob--2" aria-hidden="true" />
            <div className="lp-hero__blob lp-hero__blob--3" aria-hidden="true" />

            <div className="lp-container">
                <div className="lp-hero__inner">
                    <div className="lp-hero__copy">
                        {user ? (
                            <span className="lp-hero__eyebrow">
                                <span className="lp-hero__pulse" aria-hidden="true" />
                                Vítejte zpět, {user.username.split("@")[0]}
                            </span>
                        ) : (
                            <span className="lp-hero__eyebrow">
                                <span className="lp-hero__pulse" aria-hidden="true" />
                                {data?.eyebrow || "B2B Technological Insight"}
                            </span>
                        )}

                        <h1 className="lp-hero__title">{renderTitle(title)}</h1>

                        <p className="lp-hero__text">
                            {user
                                ? "Váš inteligentní systém pro správu dokumentace a AI auditování je připraven. Analyzujte své PDF soubory během několika sekund."
                                : (data?.paragraph || "Načítám informace...")
                            }
                        </p>

                        {/* Key benefits (facts) under the hero paragraph — mirrors the static markup in public/index.html */}
                        {!user && data?.benefits && data.benefits.length > 0 && (
                            <ul className="lp-hero__benefits">
                                {data.benefits.map(benefit => (
                                    <li key={benefit} className="lp-hero__benefit">
                                        <Check size={16} strokeWidth={3} /> {benefit}
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="lp-hero__actions">
                            {!user ? (
                                <>
                                    <a href="#contact" className="lp-btn lp-btn--primary lp-btn--lg">
                                        {data?.cta || "Domluvit konzultaci"} <ArrowRight size={20} />
                                    </a>
                                    <a href="#services" className="lp-btn lp-btn--ghost lp-btn--lg">
                                        {data?.ctaSecondary || "Prohlédnout služby"}
                                    </a>
                                </>
                            ) : (
                                <>
                                    <button type="button" onClick={() => setView("scoring")} className="lp-btn lp-btn--primary lp-btn--lg">
                                        <BarChart3 size={22} /> Spustit screening
                                    </button>
                                    <button type="button" onClick={() => setView("portfolio")} className="lp-btn lp-btn--ghost lp-btn--lg">
                                        <LayoutDashboard size={20} /> Dashboard
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="lp-hero__visual" aria-hidden="true">
                        <IntegrationNetwork />
                    </div>
                </div>
            </div>

            <a href="#features" className="lp-hero__scroll" aria-label="Posunout na další sekci">
                Dolů
                <span />
            </a>
        </header>
    )
}
